// ============================================================
// single-flight.ts
// ============================================================
// Collapse concurrent identical async work onto one execution.
//
// WHY THIS EXISTS: a resolved-value cache is written only when a call RETURNS,
// so every caller that starts in the same tick misses it and issues its own
// duplicate request. That is the exact shape of the has_feature_access storm
// documented in feature-access-cache.ts — 5,438 calls in one hour, ~10% of all
// database traffic. The JWT hook (use-feature-access.ts) fixed it with an
// in-flight promise map it owns inline; the PIN/kiosk hook never got the same
// treatment, and it is the worse offender: EmployeeLimitedView mounts SIX
// useFeatureAccessForPIN instances at once and each asks limited-feature-access
// for the WHOLE feature list, which fans out to one has_feature_access RPC per
// feature server-side.
//
// Pulled out here rather than copied inline a second time so the behaviour is
// testable on its own and the next caller with the same problem can reuse it.
//
// This is deliberately NOT a cache: an entry is dropped as soon as it settles,
// so a later call re-runs the work. Caching is a separate concern the callers
// already handle (in-memory Map + localStorage tier).

export interface SingleFlight<T> {
  /**
   * Run `work` for `key`, or join the run already in progress for that key.
   * Every joiner settles with the same value or the same error.
   */
  run(key: string, work: () => Promise<T>): Promise<T>;
  /** How many executions are currently in flight. Exposed for tests. */
  readonly size: number;
  /** Forget every in-flight entry, so the next `run` starts fresh work. */
  clear(): void;
}

export function createSingleFlight<T>(): SingleFlight<T> {
  const pending = new Map<string, Promise<T>>();

  return {
    run(key: string, work: () => Promise<T>): Promise<T> {
      const existing = pending.get(key);
      if (existing) return existing;

      let started: Promise<T>;
      try {
        started = work();
      } catch (err) {
        // `work` threw synchronously (a bad argument, say). Nothing is in
        // flight, so park nothing — otherwise the key would hold a rejected
        // promise that every later caller joins forever.
        return Promise.reject(err);
      }

      // Evict on settle in BOTH directions. Using .then(ok, err) rather than
      // .finally() keeps this working on the older Promise shims some kiosk
      // iPads still load, and rethrowing preserves the rejection for joiners.
      const tracked = started.then(
        (value) => {
          pending.delete(key);
          return value;
        },
        (err) => {
          pending.delete(key);
          throw err;
        },
      );

      pending.set(key, tracked);
      return tracked;
    },

    get size() {
      return pending.size;
    },

    clear() {
      pending.clear();
    },
  };
}
